import React, { useCallback, useEffect, useRef, useState } from 'react';
import Doodle from './Doodle';
import type { DoodleName } from './doodleArt';
import NavMark from './NavMark';
import useCardboardTilt from './useCardboardTilt';
import './sectionSticker.css';

/** The Bearri page's starter-card palette: one tinted paper, its ink and its cut edge per tone. */
export type StickerTone = 'blue' | 'rose' | 'ochre' | 'plum' | 'sage' | 'rust';
export const STICKER_TONES: readonly StickerTone[] = ['blue', 'rose', 'ochre', 'plum', 'sage', 'rust'];

/** What the drawing is, in one word, for the button's name. A drawing added to doodleArt.ts
 *  later reads by its own name until it is given a better word here. */
const NOUN: Partial<Record<string, string>> = {
  tray: 'out-tray', envelope: 'envelope', folder: 'folder', tag: 'tag', ticket: 'ticket', jar: 'jar',
  lens: 'magnifier', pen: 'pen', parcel: 'parcel', stamp: 'stamp', drawer: 'card drawer', clip: 'paperclip',
  camera: 'camera', screen: 'screen', bookmark: 'notebook',
  megaphone: 'megaphone', gift: 'present', tee: 't-shirt',
};
export const stickerNoun = (name: DoodleName) => NOUN[name] ?? String(name).replace(/[-_]+/g, ' ');

/** A safety net only: the spin ends on its own animationend (1.25s in sectionSticker.css). */
const SPIN_SAFETY_MS = 2000;
/** The four walls of the card's thickness, plus the chord across the ear (bottom-right). */
const WALLS = ['top', 'left', 'bottom', 'right', 'ear'] as const;

/** Motion stays at rest where the person asked for quiet, or in a still preview. */
const quietHere = (el: HTMLElement | null) =>
  Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)').matches)
  || Boolean(el?.closest('[data-quiet=true]'));

/**
 * A section's engraving, die-cut onto a cardboard sticker.
 *
 * The page's hero pin is a cardboard cut-out you can tilt and spin; every section of the
 * dashboard and of a creator's profile now carries one small sticker of the same stock, so
 * the sections read apart at a glance and each one has something to play with. The front
 * is the Bearri page's starter card in miniature — tinted paper on a white die margin, the
 * drawing printed in the tone's ink, one ear cut at the bottom-right — laid a few degrees
 * off square. Under the hand it tips toward the pointer (the same tilt as the pin, from
 * useCardboardTilt) and its kraft edge shows; a click turns it once round to show the
 * cardboard back, stamped with Bearri's paw, and brings it home. A turn that has started
 * finishes before another can begin.
 *
 * It says nothing the heading beside it does not already say, so it is not a figure for a
 * screen reader — but it is a real button, named, so a keyboard can spin it too.
 * Reduced motion: it neither tilts nor turns.
 */
export default function SectionSticker({ doodle, tone = 'blue', label, className = '' }: {
  doodle: DoodleName;
  tone?: StickerTone;
  /** The section it belongs to ("Your sponsors"), for the button's name. */
  label: string;
  className?: string;
}) {
  const ref = useRef<HTMLButtonElement>(null);
  const tilt = useCardboardTilt(ref, true);
  const [spinning, setSpinning] = useState(false);
  const [spins, setSpins] = useState(0);
  const spinLock = useRef(false);
  const endSpin = useCallback(() => { spinLock.current = false; setSpinning(false); }, []);
  useEffect(() => {
    if (!spinning) return;
    // animationend can be lost (a tab hidden mid-turn, the preference changed): never stay locked
    const timer = window.setTimeout(endSpin, SPIN_SAFETY_MS);
    return () => window.clearTimeout(timer);
  }, [spinning, endSpin]);

  const spin = () => {
    if (spinLock.current || quietHere(ref.current)) return;
    spinLock.current = true;
    setSpinning(true);
    setSpins(n => n + 1);
  };
  const depth = spinning || tilt.active;
  return (
    <button ref={ref} type="button" className={`br-section-sticker ${className}`.trim()}
      data-sticker-tone={tone} data-spinning={spinning} data-depth={depth} data-spins={spins}
      aria-label={`Spin the ${stickerNoun(doodle)} — ${label}`} title="Give it a spin"
      onClick={spin} {...tilt.handlers}>
      <StickerStock surfaceRef={tilt.surfaceRef} depth={depth} spinning={spinning} onSpinEnd={endSpin}
        front={<Doodle name={doodle} size="sm" />}
        back={<NavMark name="paw" className="br-section-sticker__paw" />} />
    </button>
  );
}

/**
 * The stock every sticker is cut from: laid a few degrees off square, its cast on the paper,
 * the hand's tilt, the card itself (the rotor), its kraft walls while they can be seen, the
 * printed front and — only for a sticker that turns — its cardboard back.
 * SectionSticker prints an engraving on it; PrintSticker prints a photo or a logo.
 */
export function StickerStock({ surfaceRef, depth, spinning = false, onSpinEnd, front, back }: {
  surfaceRef: React.Ref<HTMLSpanElement>;
  /** Draw the thickness: only while the card is tipped or turning. */
  depth: boolean;
  spinning?: boolean;
  onSpinEnd?: () => void;
  front: React.ReactNode;
  back?: React.ReactNode;
}) {
  return (
    <span className="br-section-sticker__pose" aria-hidden="true">
      <span className="br-section-sticker__cast" />
      <span ref={surfaceRef} className="br-section-sticker__tilt">
        <span className={`br-section-sticker__card${spinning ? ' br-section-sticker__card--spin' : ''}`}
          onAnimationEnd={event => { if (event.target === event.currentTarget) onSpinEnd?.(); }}>
          {/* the stock's thickness is only drawn while it can be seen */}
          {depth && WALLS.map(wall => <span key={wall} className={`br-section-sticker__edge br-section-sticker__edge--${wall}`} />)}
          <span className="br-section-sticker__face br-section-sticker__face--front">
            <span className="br-section-sticker__print">{front}</span>
          </span>
          {back && <span className="br-section-sticker__face br-section-sticker__face--back">{back}</span>}
        </span>
      </span>
    </span>
  );
}
